import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import formatMoney from '@/lib/formatMoney';
import { toast } from 'sonner';
import Badge from '../UI/Badge';
import Button from '../UI/Button';
import DeleteStrategyModal from '../Strategies/DeleteStrategyModal';
import StopStrategyModal from '../Strategies/StopStrategyModal';
import StrategyToolsDropdown from '../Strategies/StrategyToolsDropdown';
import NotificationPrefsModal from '../Strategies/NotificationPrefsModal';
import { strategiesAPI } from '../../services/api';

const StrategyDetails = ({ strategy, positions, closedToday = [], strategies = [], selectedStrategyId, onStrategyChange, onStrategyUpdate, livePrice }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getStatusVariant = (status) => {
    const map = {
      running: 'success',
      stopped: 'neutral',
      paused: 'warning',
      error: 'danger',
      archived: 'neutral',
    };
    return map[status] || 'neutral';
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleAction = async (actionFn, actionName) => {
    setActionLoading(actionName);
    try {
      await actionFn(strategy.id);
      toast.success(`Strategy ${actionName} successful`);
      onStrategyUpdate?.();
    } catch (err) {
      toast.error(`Failed to ${actionName}: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchiveConfirm = async ({ liquidate } = {}) => {
    try {
      await strategiesAPI.archive(strategy.id, { liquidate });
      setArchiveModalOpen(false);
      toast.success('Strategy archived');
      onStrategyUpdate?.();
    } catch (err) {
      toast.error(`Failed to archive: ${err.message}`);
    }
  };

  const handleStopConfirm = () => {
    setStopModalOpen(false);
    onStrategyUpdate?.();
  };

  const totalPositionValue = positions.reduce((sum, pos) => {
    return sum + parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price);
  }, 0);

  const totalUnrealized = positions.reduce((sum, pos) => {
    return sum + parseFloat(pos.unrealized_pnl || 0);
  }, 0);

  const totalRealized = positions.reduce((sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0)
    + closedToday.reduce((sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0);

  const totalPnL = totalUnrealized + totalRealized;

  const canSwitch = strategies.length > 1 && onStrategyChange;
  const displayName = strategy?.name || strategies.find((s) => s.id === selectedStrategyId)?.name || 'Loading...';

  const dropdownMenu = dropdownOpen && (
    <div className="absolute top-full left-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Switch Strategy</p>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {strategies.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { onStrategyChange(s.id); setDropdownOpen(false); }}
            className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
              s.id === (strategy?.id || selectedStrategyId) ? 'bg-primary/10' : 'hover:bg-white/[0.04]'
            }`}
          >
            <div className="min-w-0">
              <p className={`text-sm font-semibold truncate ${s.id === (strategy?.id || selectedStrategyId) ? 'text-primary' : 'text-foreground'}`}>
                {s.name}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {s.type?.replace('_', ' ')} {s.parameters?.symbol ? `· ${s.parameters.symbol}` : ''}
              </p>
            </div>
            <Badge variant={getStatusVariant(s.status)}>{s.status}</Badge>
          </button>
        ))}
      </div>
    </div>
  );

  if (!strategy) {
    return (
      <div className="bg-card border border-border rounded-lg px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => canSwitch && setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 text-lg font-bold text-foreground ${canSwitch ? 'cursor-pointer hover:text-primary transition-colors' : 'cursor-default'}`}
            >
              {displayName}
              {canSwitch && (
                <svg className={`w-4 h-4 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
            {dropdownMenu}
          </div>
          <div className="h-4 w-32 bg-accent/10 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const params = strategy.parameters || {};
  const currentPrice = livePrice != null ? livePrice
    : positions.length > 0
      ? parseFloat(positions[0].current_price || positions[0].entry_price)
      : null;
  const isRunning = strategy.status === 'running';
  const isStopped = strategy.status === 'stopped';
  const isPaused = strategy.status === 'paused';
  const isError = strategy.status === 'error';
  const isArchived = strategy.status === 'archived';

  const paramEntries = [
    params.gridMode && ['Mode', params.gridMode],
    params.gridLevels && ['Levels', params.gridLevels],
    params.lowerBound != null && ['Lower', formatMoney(params.lowerBound)],
    params.upperBound != null && ['Upper', formatMoney(params.upperBound)],
    params.orderSize && ['Size', `${params.orderSize} shr`],
    params.rebalanceInterval && ['Rebal', `${params.rebalanceInterval}s`],
    params.lookbackPeriod && ['Lookback', `${params.lookbackPeriod}p`],
    params.entryThreshold != null && ['Entry', params.entryThreshold],
    params.exitThreshold != null && ['Exit', params.exitThreshold],
    params.stopLoss != null && ['SL', `${(params.stopLoss * 100).toFixed(1)}%`],
    params.takeProfit != null && ['TP', `${(params.takeProfit * 100).toFixed(1)}%`],
    params.maxPositions != null && ['Max Pos', params.maxPositions],
  ].filter(Boolean);

  return (
    <>
      <div className="bg-card border border-border rounded-lg px-4 py-3">
        {/* Row 1: Name + badges + symbol | financial stats */}
        <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1">
          <div className="flex items-center gap-3 w-full">
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => canSwitch && setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 text-lg font-bold text-foreground ${canSwitch ? 'cursor-pointer hover:text-primary transition-colors' : 'cursor-default'}`}
              >
                {strategy.name}
                {canSwitch && (
                  <svg className={`w-4 h-4 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              {dropdownMenu}
            </div>
            <Badge variant={getStatusVariant(strategy.status)}>{strategy.status}</Badge>
            <Badge variant={strategy.trading_mode === 'paper' ? 'info' : 'warning'}>
              {strategy.trading_mode === 'paper' ? 'PAPER' : 'LIVE'}
            </Badge>
            <div className="ml-auto flex items-baseline gap-2.5">
              <span className="text-lg sm:text-2xl font-black tracking-tight text-primary">{params.symbol || 'N/A'}</span>
              {currentPrice != null && (
                <span className="text-base sm:text-lg font-semibold text-foreground">{formatMoney(currentPrice)}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <Stat label="Value" value={`$${totalPositionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <Sep />
            <Stat label="Unrealized" value={formatMoney(totalUnrealized, { sign: true })} valueClass={totalUnrealized >= 0 ? 'text-green-400' : 'text-red-400'} />
            <Sep />
            <Stat label="Realized" value={formatMoney(totalRealized, { sign: true })} valueClass={totalRealized >= 0 ? 'text-green-400' : 'text-red-400'} />
            <Sep />
            <Stat label="Total P&L" value={formatMoney(totalPnL, { sign: true })} valueClass={totalPnL >= 0 ? 'text-green-400' : 'text-red-400'} />
            <Sep />
            <Stat label="Positions" value={positions.length} />
            <span className="hidden sm:contents">
              <Sep />
              <Stat label="Provider" value={strategy.provider} />
              {strategy.executor_enabled != null && (
                <>
                  <Sep />
                  <Stat
                    label="Executor"
                    value={strategy.executor_enabled ? 'ON' : 'OFF'}
                    valueClass={strategy.executor_enabled ? 'text-green-400' : 'text-muted-foreground'}
                  />
                </>
              )}
              {strategy.last_tick_at && (
                <>
                  <Sep />
                  <Stat label="Last Tick" value={new Date(strategy.last_tick_at).toLocaleTimeString()} />
                </>
              )}
              {strategy.tick_interval_seconds && (
                <>
                  <Sep />
                  <Stat label="Interval" value={`${strategy.tick_interval_seconds}s`} />
                </>
              )}
            </span>
          </div>
        </div>

        {/* Row 2: Strategy parameters + Action buttons */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 mt-2 pt-2 border-t border-white/5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {paramEntries.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground/60 uppercase tracking-wider font-medium">{strategy.type?.replace('_', ' ')}</span>
                {paramEntries.map(([label, value]) => (
                  <Stat key={label} label={label} value={value} small />
                ))}
              </>
            )}
          </div>

          {/* Action buttons */}
          {!isArchived && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Start / Resume */}
              {(isStopped || isPaused || isError) && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleAction(isPaused ? strategiesAPI.resume : strategiesAPI.start, isPaused ? 'resumed' : 'started')}
                  loading={actionLoading === 'started' || actionLoading === 'resumed'}
                  disabled={!!actionLoading}
                  title={isPaused ? 'Resume' : 'Start'}
                >
                  {isPaused ? 'Resume' : 'Start'}
                </Button>
              )}

              {/* Pause */}
              {isRunning && (
                <Button
                  size="sm"
                  variant="warning"
                  onClick={() => handleAction(strategiesAPI.pause, 'paused')}
                  loading={actionLoading === 'paused'}
                  disabled={!!actionLoading}
                  title="Pause"
                >
                  Pause
                </Button>
              )}

              {/* Stop */}
              {(isRunning || isPaused || isError) && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setStopModalOpen(true)}
                  disabled={!!actionLoading}
                  title="Stop"
                >
                  Stop
                </Button>
              )}

              {/* Edit */}
              {(isStopped || isError) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/strategies/builder/${strategy.id}`)}
                  title="Edit Strategy"
                >
                  Edit
                </Button>
              )}

              {/* Archive */}
              {(isStopped || isError) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setArchiveModalOpen(true)}
                  disabled={!!actionLoading}
                  title="Archive Strategy"
                >
                  Archive
                </Button>
              )}

              {/* Tools */}
              <StrategyToolsDropdown
                strategy={strategy}
                mode="floor"
                onDuplicate={(newStrategy) => {
                  const newId = newStrategy?.id;
                  if (newId) navigate(`/strategies/trading-floor/${newId}`);
                  else onStrategyUpdate?.();
                }}
                onNotifications={() => setNotifModalOpen(true)}
              />
            </div>
          )}
        </div>
      </div>

      <StopStrategyModal
        strategy={stopModalOpen ? strategy : null}
        open={stopModalOpen}
        onClose={() => setStopModalOpen(false)}
        onConfirm={handleStopConfirm}
      />

      <DeleteStrategyModal
        strategy={archiveModalOpen ? strategy : null}
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchiveConfirm}
        mode="archive"
      />

      <NotificationPrefsModal
        open={notifModalOpen}
        onClose={() => setNotifModalOpen(false)}
        strategyId={strategy?.id}
      />
    </>
  );
};

const Sep = () => <span className="text-border/50">|</span>;

const Stat = ({ label, value, valueClass, small }) => (
  <span className={small ? 'text-xs' : ''}>
    <span className="text-muted-foreground/60">{label}</span>{' '}
    <span className={`font-semibold ${valueClass || 'text-foreground'}`}>{value}</span>
  </span>
);

export default StrategyDetails;
